// Transport DTOs — Envío de conceptos de cortes mensuales a remuneraciones (ASIS-4971 / ASIS-4956)

// Sistema de remuneraciones de destino. Diseñado para extenderse a otros destinos (p. ej. 'BUK')
// en el futuro; hoy el backend solo acepta 'REX'.
export type PayrollTargetSystem = 'REX';

// Desenlace por corte devuelto por el backend. Slugs estables que coinciden con
// MonthlyCutsPayrollSubmissionOutcomeConstants del backend.
export type PayrollSubmissionOutcome = 'sent' | 'skipped_closed' | 'error' | 'not_found';

// POST /v1/monthlycuts/payroll-submissions
export interface LC_SendMonthlyCutsToPayrollIn {
  monthlyCutIds: number[];
  // opcional; si se omite el backend asume 'REX'. Coincide con string? del backend.
  targetSystem?: string;
}

// Estados del job asíncrono de envío a remuneraciones. Coinciden con AsyncJobStatusConstants del
// backend. Centralizados aquí para no repartir literales por el hook/tests.
export const PAYROLL_JOB_STATUS = {
  PENDING: 'Pendiente',
  IN_PROGRESS: 'En Proceso',
  COMPLETED: 'Completado',
  FAILED: 'Fallido',
  STOPPED: 'Detenido',
  EXPIRED: 'Expirado',
} as const;

export type PayrollJobStatus = (typeof PAYROLL_JOB_STATUS)[keyof typeof PAYROLL_JOB_STATUS];

// Estados terminales del job; al alcanzarlos se detiene el sondeo (polling).
export const TERMINAL_PAYROLL_JOB_STATUSES: ReadonlySet<string> = new Set<string>([
  PAYROLL_JOB_STATUS.COMPLETED,
  PAYROLL_JOB_STATUS.FAILED,
  PAYROLL_JOB_STATUS.STOPPED,
  PAYROLL_JOB_STATUS.EXPIRED,
]);

// Respuesta 202 del POST: el backend encoló el envío y devuelve el identificador del job para
// sondear su resultado. status arranca en 'Pendiente'.
export interface LC_SubmitPayrollSubmissionJobOut {
  jobId: string;
  status: string;
}

// GET /v1/monthlycuts/payroll-submissions/{jobId}
// Estado del job de envío. `result` viene poblado solo cuando status === 'Completado'.
export interface LC_GetPayrollSubmissionResultOut {
  jobId: string;
  status: string;
  progress: number;
  errorMessage?: string;
  result?: LC_SendMonthlyCutsToPayrollOut;
}

export interface LC_SendMonthlyCutToPayrollResultOut {
  monthlyCutId: number;
  companyName?: string; // coincide con string? del backend
  outcome: PayrollSubmissionOutcome;
  detail?: string; // coincide con string? del backend
  conceptCount?: number; // coincide con int? del backend
}

export interface LC_SendMonthlyCutsToPayrollOut {
  targetSystem: string;
  results: LC_SendMonthlyCutToPayrollResultOut[];
  sentCount: number;
  skippedClosedCount: number;
  errorCount: number;
  notFoundCount: number;
  totalProcessed: number;
}

// --- Modelo de dominio para la UI (resultado por corte en el diálogo de resultado) ---
export interface PayrollSubmissionResult {
  monthlyCutId: number;
  companyName: string | null;
  outcome: PayrollSubmissionOutcome;
  detail: string | null;
  conceptCount: number | null;
}

export interface PayrollSubmissionSummary {
  targetSystem: string;
  results: PayrollSubmissionResult[];
  sentCount: number;
  skippedClosedCount: number;
  errorCount: number;
  notFoundCount: number;
  totalProcessed: number;
}

// Entrada de dominio para disparar el envío desde la UI.
// targetSystem opcional: si se omite, el backend asume 'REX'.
export interface SendMonthlyCutsToPayrollInput {
  monthlyCutIds: number[];
  targetSystem?: PayrollTargetSystem;
}

// GET /v1/monthlycuts/payroll-submissions/availability
// Indica si la empresa autenticada tiene una integración de remuneraciones activa. La UI la usa
// para deshabilitar (con tooltip) la acción "Enviar a remuneraciones" cuando no hay integración.
export interface LC_GetPayrollSubmissionAvailabilityOut {
  available: boolean;
  targetSystem: string;
}

// Modelo de dominio para la UI: disponibilidad de la integración de remuneraciones.
export interface PayrollSubmissionAvailability {
  available: boolean;
  targetSystem: string;
}

// Código de error backend (HTTP 404) cuando la empresa no tiene integración de remuneraciones
// activa. La opción se muestra deshabilitada con tooltip en la UI; este código permite distinguir
// si el backend se invoca de todos modos. Centralizado aquí para no duplicar el literal.
export const PAYROLL_INTEGRATION_NOT_ACTIVE_CODE = 'NOT_FOUND_PAYROLL_INTEGRATION';
